import React from "react";
import PageTemplate from "../../../components/PageTemplate";
import BackButton from "../../../components/navigation/BackButton";
import { LdsBadge, type LdsBadgeProps } from "../../../components/lds/LdsBadge";
import SlidePageTransition from "../../../components/transitions/SlidePageTransition";

type BadgeTone = "success" | "warn" | "danger" | "neutral" | "info";

export const pageShellClass =
  "mx-auto rounded-[30px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_28px_90px_rgba(15,23,42,0.12)] backdrop-blur dark:border-white/10 dark:bg-slate-950/80 sm:p-6";

export const cardEntryClass = "motion-safe:animate-[fadeIn_220ms_ease-out]";

export const listTableClass =
  "relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white/95 shadow-[0_24px_70px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-slate-950/70";

export const listSectionClass =
  "relative overflow-hidden rounded-[28px] border border-slate-200/80 bg-white/95 shadow-[0_24px_70px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-slate-950/70";

export const listRowClass =
  "group grid cursor-pointer gap-4 px-5 py-4 transition hover:bg-slate-50/80 motion-safe:hover:-translate-y-0.5 motion-reduce:hover:translate-y-0 dark:hover:bg-white/[0.035] lg:items-center";

interface LdsPageProps {
  children: React.ReactNode;
  maxWidth?: string;
}

export const LdsPage = ({ children, maxWidth = "max-w-7xl" }: LdsPageProps) => (
  <PageTemplate title={null} wrapperClassName={`${maxWidth} mx-auto`}>
    <SlidePageTransition>
      <div className={pageShellClass}>{children}</div>
    </SlidePageTransition>
  </PageTemplate>
);

interface HeaderProps {
  eyebrow: string;
  title: string;
  description?: string;
  action?: React.ReactNode;
  meta?: React.ReactNode;
}

export const LdsHeader = ({ eyebrow, title, description, action, meta }: HeaderProps) => (
  <header className={`${cardEntryClass} flex flex-col gap-4 rounded-[30px] border border-indigo-100/80 bg-gradient-to-r from-indigo-50/90 via-white to-sky-50/75 p-6 shadow-[0_20px_55px_rgba(79,70,229,0.12)] dark:border-indigo-400/20 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/20 sm:p-8 md:flex-row md:items-center md:justify-between`}>
    <div className="min-w-0">
      <p className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">{eyebrow}</p>
      <h1 className="mt-1 break-words text-2xl font-semibold text-slate-950 dark:text-white sm:text-3xl">{title}</h1>
      {description ? <p className="mt-2 max-w-3xl text-sm text-slate-600 dark:text-slate-300">{description}</p> : null}
      {meta ? <div className="mt-3 flex flex-wrap gap-2">{meta}</div> : null}
    </div>
    {action ? <div className="flex shrink-0 flex-wrap items-center gap-2">{action}</div> : null}
  </header>
);

export { BackButton };

export const ActionButton = ({
  children,
  onClick,
  type = "button",
  tone = "primary",
  disabled = false,
  title,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  type?: "button" | "submit";
  tone?: "primary" | "secondary" | "danger" | "success";
  disabled?: boolean;
  title?: string;
}) => {
  const tones = {
    primary: "border-indigo-200 bg-indigo-50 text-indigo-700 hover:border-indigo-300 hover:text-indigo-900 dark:border-indigo-500/40 dark:bg-indigo-400/10 dark:text-indigo-100",
    secondary: "border-slate-200 bg-white text-slate-700 hover:border-indigo-200 hover:text-indigo-700 dark:border-white/10 dark:bg-white/10 dark:text-slate-100",
    danger: "border-red-200 bg-red-50 text-red-700 hover:border-red-300 hover:text-red-900 dark:border-red-500/40 dark:bg-red-400/10 dark:text-red-100",
    success: "border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-300 hover:text-emerald-900 dark:border-emerald-500/40 dark:bg-emerald-400/10 dark:text-emerald-100",
  };
  return (
    <button
      type={type}
      onClick={(event) => {
        event.stopPropagation();
        onClick?.();
      }}
      disabled={disabled}
      title={title}
      className={`lumen-glow-sheen inline-flex items-center justify-center rounded-full border px-4 py-2 text-sm font-semibold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60 ${tones[tone]}`}
    >
      {children}
    </button>
  );
};

export const IconActionButton = ({
  children,
  label,
  onClick,
  tone = "secondary",
  disabled = false,
}: {
  children: React.ReactNode;
  label: string;
  onClick: () => void;
  tone?: "secondary" | "danger";
  disabled?: boolean;
}) => {
  const tones = {
    secondary: "border-slate-200 bg-white text-slate-600 hover:border-indigo-200 hover:text-indigo-700 dark:border-white/10 dark:bg-white/10 dark:text-slate-200 dark:hover:text-indigo-200",
    danger: "border-red-100 bg-red-50 text-red-600 hover:border-red-200 hover:text-red-700 dark:border-red-400/30 dark:bg-red-400/10 dark:text-red-200",
  };
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      disabled={disabled}
      className={`inline-flex h-10 w-10 items-center justify-center rounded-full border text-sm shadow-sm transition hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:hover:translate-y-0 ${tones[tone]}`}
    >
      {children}
    </button>
  );
};

export const ListSection = ({
  title,
  count,
  children,
}: {
  title: React.ReactNode;
  count: React.ReactNode;
  children: React.ReactNode;
}) => (
  <section className={`${cardEntryClass} ${listSectionClass}`}>
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4 dark:border-white/10">
      <h2 className="text-sm font-semibold uppercase text-slate-600 dark:text-slate-300">{title}</h2>
      <StatusBadge tone="neutral">{count}</StatusBadge>
    </div>
    <div className="divide-y divide-slate-100 dark:divide-white/10">{children}</div>
  </section>
);

export const AvatarBlock = ({ children, tone = "indigo" }: { children: React.ReactNode; tone?: "indigo" | "sky" | "emerald" }) => {
  const tones = {
    indigo: "bg-indigo-50 text-indigo-700 ring-indigo-100 dark:bg-indigo-400/15 dark:text-indigo-100 dark:ring-indigo-300/20",
    sky: "bg-sky-50 text-sky-700 ring-sky-100 dark:bg-sky-400/15 dark:text-sky-100 dark:ring-sky-300/20",
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-100 dark:bg-emerald-400/15 dark:text-emerald-100 dark:ring-emerald-300/20",
  };
  return (
    <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-sm font-bold uppercase shadow-sm ring-1 ${tones[tone]}`}>
      {children}
    </div>
  );
};

type StatusBadgeProps = Omit<LdsBadgeProps, "tone"> & { tone?: BadgeTone };

export const StatusBadge = ({ tone = "neutral", ...props }: StatusBadgeProps) => (
  <LdsBadge tone={tone === "warn" ? "warning" : tone} {...props} />
);

export const SearchPanel = ({ children }: { children: React.ReactNode }) => (
  <section className={`${cardEntryClass} rounded-[22px] border border-slate-200/80 bg-white/85 p-4 shadow-sm dark:border-white/10 dark:bg-white/5`}>
    {children}
  </section>
);

export const EmptyState = ({ children }: { children: React.ReactNode }) => (
  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/70 p-6 text-center text-sm text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-400">
    {children}
  </div>
);

export const Field = ({
  label,
  children,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
  key?: React.Key;
}) => (
  <label className="space-y-2">
    <span className="block text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">{label}</span>
    {children}
  </label>
);

export const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white/90 px-4 py-2 text-slate-950 shadow-sm transition focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-white/10 dark:bg-slate-950/60 dark:text-white";
